/**
 * Credit-note models (admin).
 *
 * Shapes are derived from the backend Sequelize models / Joi validators:
 *   models/credit_notes.js, models/credit_note_items.js,
 *   validators/credit-note-validator.js, docs/paths/credit-note.yaml
 *
 * Typing conventions used here (they follow how the API actually serialises):
 *  - `DECIMAL` columns come back from Postgres as **strings** (e.g. "180.00").
 *  - `REAL` columns come back as **numbers**.
 *  - `BIGINT` columns come back as **strings**, but some paths echo the value the
 *    client sent, so id-like BIGINT fields are typed `number | string`.
 */

import { SubmitResponse } from '../common/api-response.model';
import { CommissionType } from './payment-method.model';

/** Refund methods accepted by the credit-note endpoints (`refund_method`). */
export type CreditNoteRefundMethod = 'card' | 'cash' | 'tabby' | 'tamara';

//#region Persisted entities

/** A `credit_notes` row. DECIMAL columns are serialised as strings. */
export interface CreditNote {
  id: number;
  /** Set for return-sourced credit notes, null for cancellation-sourced ones. */
  return_id: number | null;
  /** Set for cancellation-sourced credit notes, null for return-sourced ones. */
  order_cancellation_id: number | string | null;
  credit_note_number: string | null;
  /** Ex-VAT subtotal. DECIMAL(10,2) → string. */
  subtotal: string | null;
  /** DECIMAL(10,2) → string. */
  vat_amount: string | null;
  /** Final refundable amount. DECIMAL(10,2) → string. */
  total_amount: string;
  /** One of {@link CreditNoteRefundMethod}; kept as `string` because the API is not enum-constrained. */
  refund_method: string | null;
  /** Commission percentage applied. DECIMAL(5,2) → string. Always "0.00" when {@link commission_type} is `fixed`. */
  commission_percentage: string;
  /**
   * How the commission was expressed when the note was raised. Absent on rows
   * created before the commission-type migration; treat as `percentage`.
   */
  commission_type?: CommissionType | null;
  /** Commission retained on the gross refund, in AED — set for BOTH commission types. DECIMAL(10,2) → string. */
  commission_amount: string;
  /** DECIMAL(10,2) → string. */
  shipping_amount: string;
  /** DECIMAL(10,2) → string. */
  return_fee: string | null;
  /** SGMART-912 — admin-entered fee, reduces the refund like `return_fee`. DECIMAL(10,2) → string. */
  handling_fee: string;
  reason: string | null;
  /** Free-text admin note captured when the credit note was raised. */
  notes: string | null;
  /** Generated asynchronously; may still be null right after creation. */
  pdf_url: string | null;
  issued_by: number | null;
  created_at?: string;
  updated_at?: string;
  credit_note_items?: CreditNoteItem[];
}

/** A `credit_note_items` row. DECIMAL columns are serialised as strings. */
export interface CreditNoteItem {
  id: number | string;
  credit_note_id: number | string;
  product_variant_id: number | string;
  quantity: number;
  /** unit_price × quantity (VAT-inclusive). DECIMAL(10,2) → string. */
  original_amount: string;
  /** DECIMAL(10,2) → string. */
  deduction_amount: string | null;
  /** original_amount − deduction_amount. DECIMAL(10,2) → string. */
  refunded_amount: string;
  /** Equal to `refunded_amount`. DECIMAL(10,2) → string. */
  total_refund_amount: string;
  created_at?: string;
  updated_at?: string;
  product_variant?: CreditNoteProductVariant | null;
}

//#endregion

//#region Create payloads

/**
 * One line of `credit_note_items` on `POST /credit-note` (return-sourced).
 * `item_return_id` is the `return_items.id` returned by `GET /credit-note`.
 */
export interface CreateCreditNoteItemPayload {
  item_return_id: number | string;
  quantity: number;
  /** VAT-inclusive AED amount deducted from this line's refund. */
  deduction_amount: number;
}

/**
 * One line of `credit_note_items` on `POST /credit-note/cancellation`.
 * `order_item_id` is the `order_items.id` returned by `GET /credit-note/cancellation`.
 */
export interface CreateCancellationCreditNoteItemPayload {
  order_item_id: number | string;
  quantity: number;
  /** VAT-inclusive AED amount deducted from this line's refund. */
  deduction_amount: number;
}

/** Fields shared by both create payloads. Optional fields are defaulted server-side. */
interface CreateCreditNoteBasePayload {
  /**
   * One of {@link CreditNoteRefundMethod}. Typed as `string` because the admin form
   * binds a plain string control to it.
   */
  refund_method: string;
  /** Fee deducted from the refund. Required by the API. */
  return_fee: number;
  /** Reason for the credit note. Required by the API (max 255 chars). */
  reason: string;
  /** Free-text admin note stored on the credit note. Optional, max 1000 chars. */
  notes?: string | null;
  /**
   * Commission retained on the gross refund. Interpreted per {@link commission_type}:
   * a 0–100 rate when `percentage`, a flat AED amount when `fixed`. Defaults to 0.
   */
  commission?: number;
  /**
   * How {@link commission} is expressed. Optional — the API defaults it to
   * `percentage`, which is also how it treats pre-migration requests that omit it.
   */
  commission_type?: CommissionType;
  /** Shipping charged back when not collected from the customer. Defaults to 0. */
  shipping_amount?: number;
  /** SGMART-912 — admin-entered handling fee, deducted from the refund. Defaults to 0. */
  handling_fee?: number;
}

/** Request body for `POST /credit-note` (credit note raised against a return). */
export interface CreateCreditNotePayload extends CreateCreditNoteBasePayload {
  return_id: number | string;
  credit_note_items: CreateCreditNoteItemPayload[];
}

/** Request body for `POST /credit-note/cancellation` (whole-order credit note). */
export interface CreateCancellationCreditNotePayload extends CreateCreditNoteBasePayload {
  order_cancellation_id: number | string;
  credit_note_items: CreateCancellationCreditNoteItemPayload[];
}

/** `data` payload returned by both create endpoints. */
export interface CreatedCreditNote {
  id: number;
  credit_note_number: string;
  /** Generated asynchronously; usually null immediately after creation. */
  pdf_url: string | null;
}

/** Envelope returned by both create endpoints (the POST responses are not unwrapped). */
export interface CreateCreditNoteResponse extends SubmitResponse {
  data?: CreatedCreditNote;
}

//#endregion

//#region GET responses

/** Product variant as projected onto credit-note sources (translations are flattened to `translation`). */
export interface CreditNoteProductVariant {
  id: number | string;
  barcode?: string | null;
  /** Flattened from `product_variant_translations` for the requested language. */
  translation?: { name?: string | null } | null;
  product_variant_gallery?: { image_thumbnail_url?: string | null } | null;
}

/** Customer details projected onto a credit-note source. */
export interface CreditNoteOrderAddress {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  mobile?: string | null;
}

/** Order summary projected onto a credit-note source. */
export interface CreditNoteOrderSummary {
  id?: number | string;
  cached_payment_method?: string | null;
  items_count?: number | null;
  /** REAL → number. */
  grand_total?: number | null;
  /**
   * Handling fee the customer paid on the order, VAT-INCLUSIVE. REAL → number.
   * Seeds the credit note's admin-editable Handling Fee field; the credit note
   * refunds it, so it ADDS to the total rather than being deducted.
   */
  handling_fee?: number | null;
  /**
   * Coupon discount on the order, VAT-INCLUSIVE. REAL → number.
   * Charged against `sub_total`, NOT baked into any line's unit price, so a credit
   * note must claw back its share or it refunds money the customer never paid.
   * (A product's sale price needs no such treatment — it IS the unit price.)
   */
  discount?: number | null;
  /** Order subtotal before the coupon — the basis the coupon is apportioned against. */
  sub_total?: number | null;
  /**
   * Coupon still un-clawed-back by this order's existing credit notes. Supplied by
   * the API so this preview caps the allocation exactly as the backend will, and the
   * figure the admin approves is the figure that gets persisted.
   */
  discount_remaining?: number | null;
  createdAt?: string;
}

/** A refund-approved, un-credited `return_items` row eligible for a credit note. */
export interface CreditNoteReturnItem {
  /** Pass this as `item_return_id` when creating the credit note. */
  id: number | string;
  return_id: number | string;
  order_id?: number | string;
  order_item_id?: number | string;
  product_variant_id: number | string;
  cache_product_name?: string | null;
  /** REAL → number. VAT-inclusive unit price the customer paid. */
  item_price: number;
  /** REAL → number. */
  cache_items_total?: number;
  quantity: number;
  /** Quantity still creditable for this return item. */
  remaining_quantity?: number;
  reason?: string | null;
  comment?: string | null;
  refund_status?: string;
  request_status?: string;
  rejected_reason?: string | null;
  refund_media?: unknown;
  credited?: boolean;
  credit_note_status?: string | null;
  product_variant?: CreditNoteProductVariant | null;
}

/** `GET /credit-note?id={returnId}` — a return plus its credit-note-eligible items. */
export interface CreditNoteReturnSource {
  id: number | string;
  order_id: number | string;
  /** REAL → number. */
  sub_total_refund?: number;
  /** REAL → number. */
  return_fee?: number;
  /** REAL → number. */
  grand_total_refund?: number;
  payment_method?: string | null;
  created_via?: string | null;
  staff_email?: string | null;
  /** DECIMAL(10,2) → string. */
  accountant_delivery_charge?: string | null;
  accountant_note?: string | null;
  tracking_number?: string | null;
  order_address?: CreditNoteOrderAddress | null;
  order?: CreditNoteOrderSummary | null;
  /** Refund-approved, un-credited return items. */
  return_items?: CreditNoteReturnItem[];
}

/** An `order_items` row projected onto a cancellation credit-note source. */
export interface CreditNoteOrderItem {
  /** Pass this as `order_item_id` when creating the credit note. */
  id: number | string;
  product_variant_id: number | string;
  /** Ordered quantity. Use {@link remaining_quantity} as the credit-note ceiling. */
  quantity: number;
  /**
   * Quantity still creditable for this order item (ordered − already credited by this
   * cancellation's existing notes). Always set by `GET /credit-note/cancellation`,
   * which also drops items whose remaining quantity has reached 0. This — not
   * `quantity` — is the max the create endpoint accepts.
   */
  remaining_quantity?: number;
  /** REAL → number. VAT-inclusive unit price the customer paid. */
  unit_price: number;
  product_variant?: CreditNoteProductVariant | null;
}

/** The order carried by a cancellation credit-note source. */
export interface CreditNoteCancellationOrder extends CreditNoteOrderSummary {
  order_address?: CreditNoteOrderAddress | null;
  order_items?: CreditNoteOrderItem[];
}

/** `GET /credit-note/cancellation?id={cancellationId}` — a confirmed cancellation and its order items. */
export interface CreditNoteCancellationSource {
  id: number | string;
  order_id: number | string;
  user_id?: number | string;
  status?: string;
  reason?: string | null;
  other?: string | null;
  coupon_code?: string | null;
  /** REAL → number. */
  subtotal?: number | null;
  /** REAL → number. */
  shipping_fee?: number | null;
  /** REAL → number. */
  discount?: number | null;
  cached_customer_name?: string | null;
  cached_payment_method?: string | null;
  is_visible?: boolean;
  invoice_url?: string | null;
  createdAt?: string;
  order?: CreditNoteCancellationOrder | null;
  /** Existing credit notes for this cancellation (empty when none). */
  credit_notes?: CreatedCreditNote[];
}

/** Either shape returned by the two credit-note GET endpoints. */
export type CreditNoteSource = CreditNoteReturnSource | CreditNoteCancellationSource;

//#endregion

/**
 * Short-lived, presigned download link for a credit note's stored PDF.
 * The raw `pdf_url` points at a private S3 bucket and cannot be opened directly.
 */
export interface CreditNotePdfLink {
  pdf_url: string;
}
